import React, { useEffect, useState } from "react";
import { Button, Label, ListBox, Select, Surface, Tabs } from "@heroui/react";
import { PortalTextArea } from "../components/form/PortalTextArea";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { navigatePortal } from "../lib/navigation";
import { showErrorToast, showSuccessToast } from "../lib/toast";

const TOPICS = [
  { id: "overview", labelKey: "help.topics.overview" },
  { id: "withdrawals", labelKey: "help.topics.withdrawals" },
  { id: "guarantees", labelKey: "help.topics.guarantees" },
  { id: "aiLabels", labelKey: "help.topics.aiLabels" },
  { id: "plans", labelKey: "help.topics.plans" },
  { id: "contact", labelKey: "help.topics.contact" }
];

const SUPPORT_HIGHLIGHTS = [
  { key: "quickSupport", tone: "portal-banner--positive" },
  { key: "customerRequirements", tone: "portal-banner--info" },
  { key: "euCompliance", tone: "portal-banner--info" }
];

const OVERVIEW_CARDS = [
  { id: "withdrawals", titleKey: "help.overview.withdrawalsTitle", bodyKey: "help.overview.withdrawalsBody" },
  { id: "guarantees", titleKey: "help.overview.guaranteesTitle", bodyKey: "help.overview.guaranteesBody" },
  { id: "aiLabels", titleKey: "help.overview.aiLabelsTitle", bodyKey: "help.overview.aiLabelsBody" },
  { id: "plans", titleKey: "help.overview.plansTitle", bodyKey: "help.overview.plansBody" }
];

const STOREFRONT_USE_CASES = [
  { id: "footerPage", stepCount: 4 },
  { id: "modalTrigger", stepCount: 4 },
  { id: "headerMenu", stepCount: 3 },
  { id: "customTexts", stepCount: 2 }
];

const GUIDE_SECTIONS = {
  guarantees: [
    { id: "themeBlock", stepCount: 3 },
    { id: "standaloneCsv", stepCount: 3 },
    { id: "exclusions", stepCount: 3 },
    { id: "garan", stepCount: 3 }
  ],
  aiLabels: [
    { id: "embed", stepCount: 3 },
    { id: "standaloneCsv", stepCount: 3 },
    { id: "markProducts", stepCount: 3 },
    { id: "appearance", stepCount: 2 }
  ],
  plans: [
    { id: "choose", stepCount: 3 },
    { id: "switch", stepCount: 2 }
  ]
};

const SUBJECT_TYPE_OPTIONS = [
  { value: "problem_with_app", labelKey: "help.subjectTypes.problemWithApp" },
  { value: "withdrawals_issue", labelKey: "help.subjectTypes.withdrawalsIssue" },
  { value: "guarantees_issue", labelKey: "help.subjectTypes.guaranteesIssue" },
  { value: "ai_labels_issue", labelKey: "help.subjectTypes.aiLabelsIssue" },
  { value: "billing_question", labelKey: "help.subjectTypes.billingQuestion" },
  { value: "storefront_issue", labelKey: "help.subjectTypes.storefrontIssue" },
  { value: "feature_request", labelKey: "help.subjectTypes.featureRequest" },
  { value: "question_or_other", labelKey: "help.subjectTypes.questionOrOther" }
];

function topicFromHash() {
  const hash = (window.location.hash || "").replace(/^#/, "");
  if (hash === "contact") return "contact";
  if (hash === "withdrawals" || hash === "storefront") return "withdrawals";
  if (hash === "guarantees") return "guarantees";
  if (hash === "ai-labels" || hash === "aiLabels") return "aiLabels";
  if (hash === "plans" || hash === "billing") return "plans";
  return null;
}

function GuideSurface({ namespace, guide, t }) {
  return (
    <Surface variant="secondary" className="portal-help-usecase-surface">
      <h3 className="text-base font-semibold">{t(`help.${namespace}.guides.${guide.id}.title`)}</h3>
      <p className="text-sm text-[var(--muted)]">{t(`help.${namespace}.guides.${guide.id}.description`)}</p>
      <ol className="help-guide-steps">
        {Array.from({ length: guide.stepCount }, (_, index) => (
          <li key={index}>{t(`help.${namespace}.guides.${guide.id}.steps.${index + 1}`)}</li>
        ))}
      </ol>
    </Surface>
  );
}

export function HelpPage() {
  const { t } = useLocale();
  const [activeTab, setActiveTab] = useState(() => topicFromHash() || "overview");
  const [subjectType, setSubjectType] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fromHash = topicFromHash();
    if (fromHash) setActiveTab(fromHash);
  }, []);

  const selectTab = (key) => {
    const next = String(key);
    setActiveTab(next);
    const hash =
      next === "aiLabels" ? "ai-labels" : next === "overview" ? "" : next;
    if (hash) {
      window.history.replaceState(null, "", `#${hash}`);
    } else {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  };

  const selectedSubjectLabel = () => {
    const option = SUBJECT_TYPE_OPTIONS.find((entry) => entry.value === subjectType);
    return option ? t(option.labelKey) : "";
  };

  const handleSubmit = async () => {
    const subject = selectedSubjectLabel();
    const trimmedMessage = message.trim();

    if (!subject || !trimmedMessage) {
      showErrorToast(t("help.validationRequired"));
      return;
    }

    setSubmitting(true);
    try {
      await apiFetch("/api/support_requests", {
        method: "POST",
        body: JSON.stringify({ subject, message: trimmedMessage })
      });
      showSuccessToast(t("help.sent"));
      setSubjectType("");
      setMessage("");
    } catch (error) {
      if (error.code === "smtp_not_configured" || error.message?.includes("smtp_not_configured")) {
        showErrorToast(t("help.smtpNotConfigured"));
      } else if (error.code === "delivery_failed") {
        showErrorToast(t("help.deliveryFailed"));
      } else {
        showErrorToast(t("help.sendError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="portal-withdrawals-layout">
      <Tabs
        selectedKey={activeTab}
        onSelectionChange={selectTab}
        orientation="vertical"
        className="portal-status-tabs w-full max-w-[220px]"
      >
        <Tabs.ListContainer>
          <Tabs.List
            aria-label={t("help.pageTitle")}
            className="portal-status-tabs__list w-full *:w-full *:justify-start *:px-3 *:py-2 *:text-sm *:font-medium *:data-[selected=true]:text-accent-foreground"
          >
            {TOPICS.map((topic) => (
              <Tabs.Tab key={topic.id} id={topic.id}>
                {t(topic.labelKey)}
                <Tabs.Indicator className="bg-accent" />
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs.ListContainer>
      </Tabs>

      <div className="portal-withdrawals-layout__content portal-page-stack">
        {activeTab === "overview" ? (
          <Surface className="portal-page-surface">
            <h2 className="portal-panel__title">{t("help.overview.title")}</h2>
            <p className="text-[var(--muted)]">{t("help.overview.intro")}</p>
            <div className="help-support-badges">
              {SUPPORT_HIGHLIGHTS.map((highlight) => (
                <span key={highlight.key} className={`portal-banner ${highlight.tone} inline-block px-3 py-1`}>
                  {t(`help.badges.${highlight.key}`)}
                </span>
              ))}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {OVERVIEW_CARDS.map((card) => (
                <Surface key={card.id} variant="secondary" className="portal-help-usecase-surface">
                  <h3 className="text-base font-semibold">{t(card.titleKey)}</h3>
                  <p className="text-sm text-[var(--muted)]">{t(card.bodyKey)}</p>
                  <Button variant="secondary" className="mt-2 w-fit" onPress={() => selectTab(card.id)}>
                    {t("help.overview.openGuide")}
                  </Button>
                </Surface>
              ))}
            </div>
            <Surface variant="secondary" className="portal-help-usecase-surface mt-3">
              <h3 className="text-base font-semibold">{t("help.overview.contactTitle")}</h3>
              <p className="text-sm text-[var(--muted)]">{t("help.overview.contactBody")}</p>
              <Button variant="primary" className="portal-accent-button mt-2 w-fit" onPress={() => selectTab("contact")}>
                {t("help.overview.contactCta")}
              </Button>
            </Surface>
          </Surface>
        ) : null}

        {activeTab === "withdrawals" ? (
          <Surface className="portal-page-surface">
            <h3 className="portal-panel__section-title">{t("help.storefront.title")}</h3>
            <p className="text-[var(--muted)]">{t("help.storefront.intro")}</p>
            <div className="portal-banner portal-banner--info">
              <h3 className="m-0 text-base font-semibold">{t("help.storefront.blocksTitle")}</h3>
              <p className="m-0 mt-2">{t("help.storefront.formBlock")}</p>
              <p className="m-0 mt-2">{t("help.storefront.triggerBlock")}</p>
              <p className="m-0 mt-2 text-sm text-[var(--muted)]">{t("help.storefront.themeNote")}</p>
            </div>
            {STOREFRONT_USE_CASES.map((useCase) => (
              <Surface key={useCase.id} variant="secondary" className="portal-help-usecase-surface">
                <h3 className="text-base font-semibold">{t(`help.storefront.useCases.${useCase.id}.title`)}</h3>
                <p className="text-sm text-[var(--muted)]">{t(`help.storefront.useCases.${useCase.id}.description`)}</p>
                <ol className="help-guide-steps">
                  {Array.from({ length: useCase.stepCount }, (_, index) => (
                    <li key={index}>{t(`help.storefront.useCases.${useCase.id}.steps.${index + 1}`)}</li>
                  ))}
                </ol>
              </Surface>
            ))}
            <Button variant="secondary" className="w-fit" onPress={() => navigatePortal("/portal/withdrawals")}>
              {t("help.storefront.openWithdrawals")}
            </Button>
          </Surface>
        ) : null}

        {activeTab === "guarantees" ? (
          <Surface className="portal-page-surface">
            <h3 className="portal-panel__section-title">{t("help.guarantees.title")}</h3>
            <p className="text-[var(--muted)]">{t("help.guarantees.intro")}</p>
            <div className="portal-banner portal-banner--info">
              <h3 className="m-0 text-base font-semibold">{t("help.guarantees.planNoteHeading")}</h3>
              <p className="m-0 mt-2">{t("help.guarantees.planNoteBody")}</p>
            </div>
            {GUIDE_SECTIONS.guarantees.map((guide) => (
              <GuideSurface key={guide.id} namespace="guarantees" guide={guide} t={t} />
            ))}
            <Button variant="secondary" className="w-fit" onPress={() => navigatePortal("/portal/guarantees")}>
              {t("help.guarantees.openModule")}
            </Button>
          </Surface>
        ) : null}

        {activeTab === "aiLabels" ? (
          <Surface className="portal-page-surface">
            <h3 className="portal-panel__section-title">{t("help.aiLabels.title")}</h3>
            <p className="text-[var(--muted)]">{t("help.aiLabels.intro")}</p>
            <div className="portal-banner portal-banner--info">
              <h3 className="m-0 text-base font-semibold">{t("help.aiLabels.art50Heading")}</h3>
              <p className="m-0 mt-2">{t("help.aiLabels.art50Body")}</p>
            </div>
            {GUIDE_SECTIONS.aiLabels.map((guide) => (
              <GuideSurface key={guide.id} namespace="aiLabels" guide={guide} t={t} />
            ))}
            <p className="text-sm text-[var(--muted)]">{t("help.aiLabels.legalNote")}</p>
            <Button variant="secondary" className="w-fit" onPress={() => navigatePortal("/portal/ai-labels")}>
              {t("help.aiLabels.openModule")}
            </Button>
          </Surface>
        ) : null}

        {activeTab === "plans" ? (
          <Surface className="portal-page-surface">
            <h3 className="portal-panel__section-title">{t("help.plans.title")}</h3>
            <p className="text-[var(--muted)]">{t("help.plans.intro")}</p>
            <div className="portal-banner portal-banner--info">
              <h3 className="m-0 text-base font-semibold">{t("help.plans.plansListTitle")}</h3>
              <ul className="help-guide-steps mt-2">
                <li>{t("help.plans.plansList.payg")}</li>
                <li>{t("help.plans.plansList.guarantees")}</li>
                <li>{t("help.plans.plansList.aiLabels")}</li>
                <li>{t("help.plans.plansList.ultimate")}</li>
                <li>{t("help.plans.plansList.ultimateAi")}</li>
              </ul>
            </div>
            {GUIDE_SECTIONS.plans.map((guide) => (
              <GuideSurface key={guide.id} namespace="plans" guide={guide} t={t} />
            ))}
            <Button variant="primary" className="portal-accent-button w-fit" onPress={() => navigatePortal("/portal/billing")}>
              {t("help.plans.openBilling")}
            </Button>
          </Surface>
        ) : null}

        {activeTab === "contact" ? (
          <div id="contact" className="scroll-mt-24">
            <Surface className="portal-page-surface">
              <h3 className="portal-panel__section-title">{t("help.formTitle")}</h3>
              <p className="text-[var(--muted)]">{t("help.intro")}</p>
              <div className="portal-field">
                <Select
                  selectedKey={subjectType || undefined}
                  onSelectionChange={(key) => setSubjectType(key ? String(key) : "")}
                  className="w-full"
                  fullWidth
                  variant="secondary"
                  placeholder={t("help.subjectPlaceholder")}
                >
                  <Label>{t("help.subjectLabel")}</Label>
                  <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      {SUBJECT_TYPE_OPTIONS.map((opt) => (
                        <ListBox.Item key={opt.value} id={opt.value} textValue={t(opt.labelKey)}>
                          {t(opt.labelKey)}
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              </div>
              <PortalTextArea label={t("help.messageLabel")} value={message} onChange={setMessage} />
              <Button variant="primary" className="portal-accent-button w-fit" isPending={submitting} onPress={handleSubmit}>
                {t("help.submit")}
              </Button>
              <p className="text-sm text-[var(--muted)]">{t("help.supportHours")}</p>
            </Surface>
          </div>
        ) : null}
      </div>
    </div>
  );
}
